// The approved Slate & Fire map art (Art/UI Redesign/UI Refresh/Map, site version 12, Oct 11 2026; the PNGs are copied
// verbatim into public/art/map/slate-fire, their manifest is art-src/slate-fire-map/manifest.json) and its Log Stockade NPC
// cities (Oct 12 2026, public/art/map/slate-fire/npc/log-stockade): the numbers that place them. No DOM here, so drawing
// (render.ts) and hit testing share exactly the same geometry and the unit tests cover it.
//
// All lengths are CSS px; `s` is the map's scale in CSS px per cell; (px, py) is a cell's centre on the canvas, from the
// production projector (geo.ts cellCentrePx: north up, wrapping).
import type { Relation } from "./relations";

/** The six relationships that have painted castles and pennants (a barbarian city — an NPC — wears its Log Stockade). */
export type CityRelation = Exclude<Relation, "barbarian">;
export const CITY_RELATIONS: readonly CityRelation[] = ["you", "guild", "ally", "nap", "enemy", "unaligned"];
export type Tier = 1 | 2 | 3 | 4;
export const TIERS: readonly Tier[] = [1, 2, 3, 4];
export const TIER_NAMES: Record<Tier, string> = { 1: "Hamlet", 2: "Town", 3: "Fortress", 4: "Citadel" };

/** Cell.level → the castle tier: 1–4 as the chunk says (the 3-bit field is clamped to 4); 0 is an empty cell. */
export function tierOf(level: number): Tier | null {
  if (!(level >= 1)) return null;
  return Math.min(4, Math.floor(level)) as Tier;
}

export const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
export const mix = (a: number, b: number, t: number) => a + (b - a) * t;
/** The reference's smoothstep: 0 at a, 1 at b. */
export function smooth(a: number, b: number, x: number): number {
  const t = clamp((x - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
}

// ------------------------------------------------------------------------------------------------ zoom treatment
/** Castles fade in across 22–35 px per cell (the reference's thresholds); markers take their place below. */
export const CASTLE_FADE: [number, number] = [22, 35];
/** Painted trees fade in across 20–28. The reference fades them across 12–28; Valor Web's region (where trees are drawn)
 *  starts at REGION_MIN = 20, so the fade is fitted inside it and the trees never pop at the region/realm boundary. */
export const TREE_FADE: [number, number] = [20, 28];
/** Far out the diamonds become points (1.4–3.3 px per cell). */
export const POINT_FADE: [number, number] = [1.4, 3.3];

export const castleAlpha = (s: number) => smooth(CASTLE_FADE[0], CASTLE_FADE[1], s);
export const treeAlpha = (s: number) => smooth(TREE_FADE[0], TREE_FADE[1], s);
/** 1 = points only, 0 = diamonds only. */
export const pointMix = (s: number) => 1 - smooth(POINT_FADE[0], POINT_FADE[1], s);
/** The mossy ground texture over its base colour: quieter far out. */
export const textureAlpha = (s: number) => 0.45 + smooth(2, 35, s) * 0.5;
/** Soft forest masses (simplified terrain): strongest where the trees have faded. */
export const patchAlpha = (s: number) => mix(0.18, 0.045, treeAlpha(s));

export const GROUND_BASE = "#59634A";
/** The 768-px ground texture repeats every 4 cells (reference: pattern scale s * 4 / 768). */
export const TEXTURE_CELLS = 4;
export const FOREST_COLOR: [number, number, number] = [32, 60, 45];

// ------------------------------------------------------------------------------------------------ castles
/** Pixel size of each tier's PNG (base and all six relationship variants share it; checked against the files in
 *  tests/unit/slateFire.test.ts). Kept here so geometry is known before the images arrive. */
export const SPRITE_PX: Record<Tier, [number, number]> = { 1: [595, 600], 2: [600, 562], 3: [588, 600], 4: [600, 599] };
export const TIER_WIDTH: Record<Tier, number> = { 1: 0.58, 2: 0.67, 3: 0.75, 4: 0.82 };
export const MAX_HEIGHT = 1.02; // × s
export const BASE_DY = 0.3; // the PNG's bottom edge sits this many cells below the cell centre
/** The opaque art's top and bottom inside each uncropped PNG (alpha > 24), as fractions of its height. */
export const SPRITE_TOP: Record<Tier, number> = { 1: 0.1167, 2: 0.1032, 3: 0.0867, 4: 0.02 };
export const SPRITE_BOTTOM: Record<Tier, number> = { 1: 0.9083, 2: 0.9324, 3: 0.94, 4: 0.98 };

export interface CastleBox {
  /** top-left of the complete, uncropped PNG */
  x: number;
  y: number;
  w: number;
  h: number;
  /** the cell centre's x (the PNG's horizontal centre) */
  cx: number;
  /** the PNG's bottom edge: py + 0.30 s */
  baseY: number;
  /** a painted world v2 castle's own painted top / bottom, as fractions of its image's height (paintedWorld.ts); the
   *  Slate & Fire castles leave these out and use SPRITE_TOP / SPRITE_BOTTOM */
  paintTop?: number;
  paintBottom?: number;
}

/** The approved placement: width = s × tier factor, height from the PNG, capped at 1.02 s, bottom-centred on py + 0.30 s. */
export function castleBox(tier: Tier, s: number, px: number, py: number, imgW = SPRITE_PX[tier][0], imgH = SPRITE_PX[tier][1]): CastleBox {
  let w = s * TIER_WIDTH[tier];
  let h = (w * imgH) / imgW;
  if (h > s * MAX_HEIGHT) {
    w *= (s * MAX_HEIGHT) / h;
    h = s * MAX_HEIGHT;
  }
  const baseY = py + s * BASE_DY;
  return { x: px - w / 2, y: baseY - h, w, h, cx: px, baseY };
}

// ------------------------------------------------------------------------------------------------ NPC cities
// The barbarian (NPC) cities wear the Log Stockade (Ben's choice, Oct 12 2026: Art/UI Redesign/UI Refresh/Map/NPC Log
// Stockade — the PNGs copied verbatim into public/art/map/slate-fire/npc/log-stockade, their manifest in
// art-src/slate-fire-map/npc-log-stockade). They are differently shaped from the castles — 1536 × 1024, wide, with their own
// foundations — so placement, hit masks, the selection glow, nameplates and status shields take the family into account.

/** Which painted family a city wears: an NPC (relationOf → "barbarian": the game's barbarian flag; a savage flag alone is
 *  not one) its Log Stockade in its authored colours, with no pennant; everyone else the castles. */
export type SpriteFamily = "castle" | "npc";
export const familyOf = (rel: Relation): SpriteFamily => (rel === "barbarian" ? "npc" : "castle");

/** Pixel size of each Log Stockade PNG: the complete image, its transparent padding included (the manifest's). */
export const NPC_PX: Record<Tier, [number, number]> = { 1: [1536, 1024], 2: [1536, 1024], 3: [1536, 1024], 4: [1536, 1024] };
/** Each stockade's painted bounds in its PNG, as the manifest measured them: alpha > 24, [left, top, right, bottom] in px,
 *  right and bottom exclusive (checked against the files in tests/unit/slateFire.test.ts). */
export const NPC_PAINTED_PX: Record<Tier, [number, number, number, number]> = {
  1: [184, 109, 1354, 924],
  2: [196, 91, 1403, 933],
  3: [84, 99, 1451, 937],
  4: [84, 70, 1450, 931],
};
/** The painted top and bottom of each stockade, as fractions of its PNG's height. */
export const NPC_TOP: Record<Tier, number> = { 1: 109 / 1024, 2: 91 / 1024, 3: 99 / 1024, 4: 70 / 1024 };
export const NPC_BOTTOM: Record<Tier, number> = { 1: 924 / 1024, 2: 933 / 1024, 3: 937 / 1024, 4: 931 / 1024 };
/** Where a status shield stands on a stockade's foundation, normalised in its PNG: at the height of the foundation's east
 *  corner (the rightmost solid pixel, alpha ≥ 96, between 0.5 and 0.75 of the height: u 0.878 / 0.912 / 0.944 / 0.943),
 *  0.06 in from it — or, for the small tier I, 0.028 in: as far in as keeps its shield clear of the gate (left edge right of
 *  the gate's right post, u 0.62) at every zoom where the stockade is drawn in full (s ≥ 35, the shield at its floor). */
export const NPC_STATUS_MOUNT: Record<Tier, [number, number]> = { 1: [0.85, 0.574], 2: [0.852, 0.614], 3: [0.884, 0.583], 4: [0.883, 0.617] };
/** A stockade's shield is the castles' scaled by how tall the stockades stand against the castles of their tiers — their
 *  painted heights are 0.665 / 0.706 / 0.627 / 0.585 of the castles' (0.645 on average) — so it sits on its foundation
 *  corner in the castles' proportion instead of outgrowing a tier I hall; never under 12 px, to stay legible. */
export const NPC_SHIELD_SCALE = 0.65;
export const NPC_SHIELD_MIN = 12;

/** The family's PNG size for a tier (geometry is known before the image arrives). */
export const spritePx = (family: SpriteFamily, tier: Tier): [number, number] => (family === "npc" ? NPC_PX[tier] : SPRITE_PX[tier]);
/** The painted top / bottom of the family's PNG, as fractions of its height (nameplates and fallback hit centres). */
export const paintedTop = (family: SpriteFamily, tier: Tier) => (family === "npc" ? NPC_TOP[tier] : SPRITE_TOP[tier]);
export const paintedBottom = (family: SpriteFamily, tier: Tier) => (family === "npc" ? NPC_BOTTOM[tier] : SPRITE_BOTTOM[tier]);

/** A city's sprite rect: the approved placement (the tier's width factor, the PNG's own aspect, 1.02 s at most, bottom on
 *  py + 0.30 s) with the family's PNG — so a stockade is the same width as a castle of its tier, and as tall as its image. */
export function cityBox(family: SpriteFamily, tier: Tier, s: number, px: number, py: number): CastleBox {
  const [w, h] = spritePx(family, tier);
  return castleBox(tier, s, px, py, w, h);
}

// ------------------------------------------------------------------------------------------------ pennants
/** Where each tier's mast stands on its roof, normalised in the uncropped PNG (x from the left, y from the top). */
export const FLAG_MOUNT: Record<Tier, [number, number]> = { 1: [0.695, 0.2], 2: [0.25, 0.18], 3: [0.576, 0.185], 4: [0.594, 0.12] };
/** The cloth PNG's native size (painted cloth only; pole and finial are drawn). */
export const FLAG_PX: [number, number] = [160, 104];
/** The whole assembly — cloth, pole, finial — is drawn at half the earlier size, scaled about the roof mount. */
export const FLAG_SCALE = 0.5;
export const FLAG_FINIAL_MIN_S = 45;
export const POLE_COLOR = "#3B2D22";
export const POLE_HIGHLIGHT = "#AE8950";
export const FINIAL_COLOR = "#C5A05D";
export const FINIAL_OUTLINE = "#5D4226";

export interface FlagGeom {
  /** the roof mount: the scale origin */
  x: number;
  bottom: number;
  /** pre-scale geometry (the reference's _drawCityFlag), drawn under scale(FLAG_SCALE) about (x, bottom) */
  top: number;
  w: number;
  h: number;
  pole: number;
  clothX: number;
  clothY: number;
  /** finial half-width, or null when the map is too far out to show it */
  finial: number | null;
}

/** `mount` (normalised in the sprite's image) and the cloth's `aspect` (h / w) default to the Slate & Fire castle's and
 *  cloth's; a painted world v2 castle passes its own (paintedWorld.ts). */
export function flagGeom(tier: Tier, rel: CityRelation, s: number, box: CastleBox, mount?: readonly [number, number], aspect?: number): FlagGeom {
  const [mx, my] = mount ?? FLAG_MOUNT[tier];
  const x = box.cx + box.w * (mx - 0.5);
  const bottom = box.baseY - box.h * (1 - my);
  const w = clamp(s * 0.2, 7, 35) * (rel === "you" ? 1.08 : 1);
  const h = w * (aspect ?? FLAG_PX[1] / FLAG_PX[0]);
  const top = bottom - h - clamp(s * 0.06, 3, 8);
  return {
    x,
    bottom,
    top,
    w,
    h,
    pole: clamp(s * 0.017, 1.1, 3),
    clothX: x - (w * 6) / 160,
    clothY: top - (h * 14) / 104,
    finial: s >= FLAG_FINIAL_MIN_S ? clamp(s * 0.012, 0.8, 2.1) : null,
  };
}

/** The flag's on-screen bounds after the half scale (for tests and the debug view). */
export function flagScreenRect(f: FlagGeom): { x: number; y: number; w: number; h: number } {
  const sx = (v: number) => f.x + (v - f.x) * FLAG_SCALE;
  const sy = (v: number) => f.bottom + (v - f.bottom) * FLAG_SCALE;
  const finialTop = f.finial !== null ? f.top - f.finial * 2.8 : f.top - 1;
  const left = Math.min(f.x - f.pole / 2, f.clothX);
  const right = Math.max(f.x + f.pole / 2, f.clothX + f.w);
  const top = Math.min(finialTop, f.clothY);
  return { x: sx(left), y: sy(top), w: (right - left) * FLAG_SCALE, h: (f.bottom - top) * FLAG_SCALE };
}

// ------------------------------------------------------------------------------------------------ selection
/** The browser-baked selection glows: 2x pixel files around a castle drawn 220 logical px wide with 135 px of padding. */
export const GLOW_UNIT = 220;
export const GLOW_PAD = 135;
export const GLOW_RES = 2;
/** Pixel size of each tier's glow PNG (manifest: logical 490 × 492 / 477 / 495 / 490 at 2x). */
export const GLOW_PX: Record<Tier, [number, number]> = { 1: [980, 984], 2: [980, 954], 3: [980, 990], 4: [980, 980] };

export function glowBox(box: CastleBox, glowW: number, glowH: number): { x: number; y: number; w: number; h: number } {
  const k = box.w / GLOW_UNIT;
  return { x: box.cx - box.w / 2 - GLOW_PAD * k, y: box.baseY - box.h - GLOW_PAD * k, w: (glowW / GLOW_RES) * k, h: (glowH / GLOW_RES) * k };
}

/** The reference's castleSelectionAura (reference/map.js), the recipe the castles' glow PNGs were baked with — the NPC
 *  stockades' glows are baked from it in the browser, each from its own PNG's alpha: the silhouette in ivory, its lower
 *  part (the footprint) fading out between AURA_FADE of the image's height, drawn 220 logical px wide with 135 px padding
 *  at 2x — three broad blurs (logical px, alpha), then a thin core from six 1–1.5 px offsets — and the unblurred image cut
 *  out of the middle ("destination-out") so the sprite drawn over it stays sharp and in its own colours. */
export const AURA_LAYERS: readonly (readonly [blur: number, alpha: number])[] = [[90, 0.54], [45.9, 0.58], [18, 0.546]];
export const AURA_CORE_ALPHA = 0.958;
export const AURA_CORE_OFFSETS: readonly (readonly [number, number])[] = [[-1.5, 0], [1.5, 0], [0, -1.5], [0, 1.5], [-1, -1], [1, -1]];
export const AURA_FADE: [number, number] = [0.78, 0.9];
/** A baked aura's logical size around an imgW × imgH image (the reference rounds up); × GLOW_RES for its pixels. For the
 *  castles this is GLOW_PX / 2 — 490 × 492 / 477 / 495 / 490 — and for the 1536 × 1024 stockades 490 × 417. */
export function auraSize(imgW: number, imgH: number): [number, number] {
  return [Math.ceil(GLOW_UNIT + GLOW_PAD * 2), Math.ceil((GLOW_UNIT * imgH) / imgW + GLOW_PAD * 2)];
}

/** The selected marker's ivory edge and glow when the map is too far out for castles. */
export const SELECT_IVORY = "#FFFFED";

// ------------------------------------------------------------------------------------------------ status shields
/** Beginner protection / truce shields stand on the right corner of the castle's grass foundation (normalised in the
 *  PNG; the foundation's right corner is at x 0.96–0.99, y 0.57–0.67 of each tier). */
export const STATUS_MOUNT: Record<Tier, [number, number]> = { 1: [0.89, 0.64], 2: [0.9, 0.6], 3: [0.9, 0.63], 4: [0.9, 0.7] };
/** The shield on its family's foundation: a castle's at STATUS_MOUNT, 0.36 s tall (15–44 px); a stockade's at
 *  NPC_STATUS_MOUNT, that size × NPC_SHIELD_SCALE (12 px at least). The same painted shields and precedence for both. */
export function statusShield(tier: Tier, s: number, box: CastleBox, family: SpriteFamily = "castle", mount?: readonly [number, number]): { x: number; y: number; h: number } {
  const npc = family === "npc";
  const [mx, my] = mount ?? (npc ? NPC_STATUS_MOUNT[tier] : STATUS_MOUNT[tier]);
  const h = npc ? clamp(s * 0.36 * NPC_SHIELD_SCALE, NPC_SHIELD_MIN, 44 * NPC_SHIELD_SCALE) : clamp(s * 0.36, 15, 44);
  return { x: box.cx + box.w * (mx - 0.5), y: box.baseY - box.h * (1 - my), h };
}

// ------------------------------------------------------------------------------------------------ distant markers
/** The diamond's half-diagonal (reference _drawMarker): grows with the zoom and the tier. */
export const markerHalf = (s: number, tier: Tier) => clamp(s * 0.44 + tier * 0.38, 2.2, 9);
/** How a city's far-out point is weighted: your own cities large, cities in no guild faint (the reference's neutral),
 *  everything else — your guild, its allies, pacts and enemies, the other guilds and the barbarians — by tier. */
export type PointKind = "you" | "faint" | "normal";
export function pointKind(rel: Relation, guildId: number): PointKind {
  return rel === "you" ? "you" : rel === "unaligned" && guildId <= 0 ? "faint" : "normal";
}
export function pointRadius(kind: PointKind, tier: Tier): number {
  return kind === "you" ? 2.3 : kind === "faint" ? 0.75 : tier === 4 ? 1.7 : 1.1;
}
export function pointAlpha(kind: PointKind, selected: boolean): number {
  return selected || kind === "you" ? 1 : kind === "faint" ? 0.38 : 0.74;
}

// ------------------------------------------------------------------------------------------------ hit testing
/** One drawn city, front-most last (the draw order: north to south, then west to east). */
export interface HitItem<T> {
  item: T;
  tier: Tier;
  /** the painted family drawn there: its own PNG, so its own mask and painted bounds */
  family: SpriteFamily;
  /** the sprite's rect, when sprites are drawn at this zoom */
  box: CastleBox | null;
  /** the marker's centre and half-size, when its target counts: markers drawn at this zoom, or a stockade's tap fallback */
  marker: { x: number; y: number; r: number } | null;
  /** the hit mask's own key when it isn't the family's per tier (a painted world v2 castle: one per relationship and tier) */
  mask?: string;
}

/** The city under a point: the front-most sprite whose painted pixels are under it wins (a sprite drawn later stands in
 *  front — at the approved placement no two sprite rects meet, the tallest being 0.82 of a cell, but a sprite's transparent
 *  padding never takes a tap from painted pixels); else the nearest sprite or marker whose target holds the point.
 *  `opaque(family, tier, u, v)` answers whether that family's PNG for the tier is opaque at normalised (u, v), or null when
 *  its mask isn't known yet. */
export function hitCities<T>(list: HitItem<T>[], px: number, py: number, opaque: (family: SpriteFamily, tier: Tier, u: number, v: number, mask?: string) => boolean | null): T | null {
  let best: T | null = null;
  let bestD = Infinity;
  for (let i = list.length - 1; i >= 0; i--) {
    const it = list[i];
    if (it.box) {
      const b = it.box;
      if (px >= b.x && px <= b.x + b.w && py >= b.y && py <= b.y + b.h) {
        const o = opaque(it.family, it.tier, (px - b.x) / b.w, (py - b.y) / b.h, it.mask);
        if (o === true) return it.item;
        // transparent padding, or a mask not loaded: a candidate, by distance to the painted centre
        const cy = b.y + (b.h * ((b.paintTop ?? paintedTop(it.family, it.tier)) + (b.paintBottom ?? paintedBottom(it.family, it.tier)))) / 2;
        const d = Math.hypot(px - b.cx, (py - cy) * 0.75) + (o === false ? b.w : 0);
        if (d < bestD) { bestD = d; best = it.item; }
        continue;
      }
    }
    if (it.marker) {
      const m = it.marker;
      const r = Math.max(m.r + 3, 9);
      if (Math.abs(px - m.x) <= r && Math.abs(py - m.y) <= r) {
        const d = Math.hypot(px - m.x, py - m.y);
        if (d < bestD) { bestD = d; best = it.item; }
      }
    }
  }
  return best;
}
